import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FlowConfigContext } from '../../../providers/FlowConfigContext';
import SVGAppStoreButton from '../../assets/svg/buttons/SVGAppStoreButton';
import SVGPlayStoreButton from '../../assets/svg/buttons/SVGPlayStoreButton';
import SVGCaretLeft from '../../assets/svg/icons/SVGCaretLeft';
import SVGDocument from '../../assets/svg/icons/SVGDocument';
import SVGFaceScan from '../../assets/svg/icons/SVGFaceScan';
import SVGInfo from '../../assets/svg/icons/SVGInfo';
import SVGShieldCheck from '../../assets/svg/icons/SVGShieldCheck';
import SVGQrCodeBW from '../../assets/svg/SVGQrCodeBW';
import SVGBot from '../../assets/svg/icons/SVGBot';
import SVGPerson from '../../assets/svg/icons/SVGPerson';
import Button, { ButtonStyle } from '../Button/Button';
import ButtonContainer from '../Button/ButtonContainer';
import Container from '../Container/Container';
import TitleContainer from '../TitleContainer/TitleContainer';
import ExplainerPanelProps, {
  ExplainerPanelDescriptionAlignment,
  ExplainerPanelIcon,
  ExplainerPanelSubDescription,
  ExplainerPanelSubDescriptionIcon,
} from './ExplainerPanelProps';

export default function ExplainerPanel(props: ExplainerPanelProps): JSX.Element {
  const {
    title,
    description,
    subDescriptionsWithIcon,
    subDescription,
    infoBoxDescription,
    infoBoxLinkDescription,
    infoBoxLinkHref,
    hideInfoBoxIcon,
    primaryButtonLabel,
    onPrimaryButtonPressed,
    secondaryButtonLabel,
    onSecondaryButtonPressed,
    footerButtonLabel,
    onFooterButtonPress,
    showDescription,
    showSubDescription,
    translationVariables,
    showSecondaryButton,
    showHeaderPanel,
    showBackButton,
    onBackButtonPressed,
    termsLink,
    privacyPolicyLink,
    biometricPolicyLink,
    dataTestId,
  } = props;

  const [termsChecked, setTermsChecked] = useState(false);
  const [biometricsPolicyChecked, setBiometricsPolicyChecked] = useState(false);
  const { networkName, chainName, getExternalLinksConfig } = useContext(FlowConfigContext);
  const { t, i18n } = useTranslation();
  const { googleStoreUrl, appleStoreUrl } = getExternalLinksConfig();
  const navigate = useNavigate();

  const handlePrimaryButtonClick = () => {
    onPrimaryButtonPressed();
  };

  const handleSecondaryButtonClick = () => {
    onSecondaryButtonPressed();
  };

  const handleFooterButtonClick = () => {
    if (onFooterButtonPress) {
      onFooterButtonPress();
    }
  };

  const handleBackButtonClick = () => {
    if (onBackButtonPressed) {
      onBackButtonPressed();
      return;
    }

    navigate(-1);
  };

  const toggleTermsChecked = () => {
    setTermsChecked(!termsChecked);
  };

  const toggleBiometricsChecked = () => {
    setBiometricsPolicyChecked(!biometricsPolicyChecked);
  };

  const handleChange = (setter: (checked: boolean) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setter(event.target.checked);
  };

  const shouldShowSecondaryButton = () => {
    // The "Use Civic App" button should only be shown for the Casper network
    return showSecondaryButton && chainName === 'casper';
  };

  const isButtonDisabled = () => {
    const isTermsChecked = !termsLink || termsChecked; // terms is not defined or is checked
    const isBiometricPolicyChecked = !biometricPolicyLink || biometricsPolicyChecked; // bioPolicy is not defined or is checked

    // if one of terms or biometric policy are defined and not checked, the buttons should be disabled.
    return !isTermsChecked || !isBiometricPolicyChecked;
  };

  let descriptionAlignment;
  switch (description?.alignment) {
    case ExplainerPanelDescriptionAlignment.LEFT:
      descriptionAlignment = 'text-left';
      break;
    case ExplainerPanelDescriptionAlignment.RIGHT:
      descriptionAlignment = 'text-right';
      break;
    case ExplainerPanelDescriptionAlignment.CENTER:
    case ExplainerPanelDescriptionAlignment.DEFAULT:
    default:
      descriptionAlignment = 'text-center';
      break;
  }

  const mapSubDescriptionIcon = (subDescriptionIcon: ExplainerPanelSubDescriptionIcon) => {
    switch (subDescriptionIcon) {
      case ExplainerPanelSubDescriptionIcon.FACE_AUTH:
        return <SVGFaceScan />;
      case ExplainerPanelSubDescriptionIcon.DOCUMENT:
        return <SVGDocument />;
      case ExplainerPanelSubDescriptionIcon.SHIELD:
        return <SVGShieldCheck />;
      case ExplainerPanelSubDescriptionIcon.BOT:
        return <SVGBot />;
      case ExplainerPanelSubDescriptionIcon.PERSON:
        return <SVGPerson />;
      default:
      case ExplainerPanelSubDescriptionIcon.DEFAULT:
        return <></>;
    }
  };

  const mapSubDescriptionAlignment = (subDescriptionToAlign: ExplainerPanelSubDescription) => {
    switch (subDescriptionToAlign.alignment) {
      case ExplainerPanelDescriptionAlignment.RIGHT:
        return 'text-right';
      case ExplainerPanelDescriptionAlignment.LEFT:
        return 'text-left';
      case ExplainerPanelDescriptionAlignment.DEFAULT:
      case ExplainerPanelDescriptionAlignment.CENTER:
      default:
        return 'text-center';
    }
  };
  let HeaderPanel;
  if (showHeaderPanel) {
    HeaderPanel = (
      <header className="flex flex-col justify-center items-center">
        <div className="flex w-full">
          <div className="flex-grow flex-basis-0">
            {showBackButton && (
              <Button
                style={ButtonStyle.INVERTED}
                onClick={handleBackButtonClick}
                disabled={false}
                extraClasses="flex-grow back-button ml-10"
                dataTestid="BACK_BUTTON"
              >
                <div className="flex text-iconTertiary uppercase text-xs flex-grow items-center">
                  <SVGCaretLeft />
                  <span className="ml-2 mt-1">{t('common.explainer.backButtonLabel')}</span>
                </div>
              </Button>
            )}
          </div>
          <div className="flex-grow flex-basis-0" />
        </div>
      </header>
    );
  }

  return (
    <Container HeaderComponent={HeaderPanel}>
      <TitleContainer center>
        <>
          {title && (
            <h1 className="mb-4" data-testid={dataTestId || title}>
              {t(title, { context: networkName })}
            </h1>
          )}
        </>
        <>
          {showDescription && description && i18n.exists(description.description, { context: networkName }) && (
            <h2 className={`flex ${descriptionAlignment}`} data-testid="VERIFY_IDENTITY_TEXT">
              {t(description.description, { context: networkName })}
            </h2>
          )}
        </>

        <div>
          {subDescriptionsWithIcon?.map((subDescriptionWithIcon) => {
            return (
              <div key={subDescriptionWithIcon.title}>
                <div className="flex mb-4 mt-2">
                  {subDescriptionWithIcon.icon && (
                    <div className="flex flex-shrink-0 pr-2 items-center justify-self-end">
                      {mapSubDescriptionIcon(subDescriptionWithIcon.icon)}
                    </div>
                  )}

                  <div
                    className={`text-lg leading-6 text-textParagraph ${mapSubDescriptionAlignment(
                      subDescriptionWithIcon,
                    )}`}
                    data-testid={subDescriptionWithIcon.title}
                  >
                    {t(subDescriptionWithIcon.title, { context: networkName })}
                  </div>
                </div>

                <>
                  {(subDescriptionWithIcon.showAppIcons || subDescriptionWithIcon.showQRCode) && (
                    <div className="-mx-0 my-4 flex justify-center items-center">
                      {subDescriptionWithIcon.showQRCode && (
                        <div
                          className={`p-1 bg-background ${subDescriptionWithIcon.showAppIcons ? 'mr-3' : ''}`}
                          data-testid="APP_QRCODE"
                        >
                          <SVGQrCodeBW />
                        </div>
                      )}
                      {subDescriptionWithIcon.showAppIcons && (
                        <>
                          <a
                            data-testid="PLAY_STORE_BUTTON"
                            className="mr-3 svg-button-link"
                            href={googleStoreUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <SVGPlayStoreButton />
                          </a>

                          <a
                            data-testid="APP_STORE_BUTTON"
                            className="svg-button-link"
                            href={appleStoreUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <SVGAppStoreButton />
                          </a>
                        </>
                      )}
                    </div>
                  )}
                </>
              </div>
            );
          })}
        </div>

        <>
          {infoBoxDescription && (
            <div className="my-1 flex w-full transform flex-row items-center justify-center rounded-lg border border-border p-4 gap-2">
              {!hideInfoBoxIcon && <SVGInfo />}
              <div className="w-full text-left">
                <div
                  className="text-sm leading-5 text-textParagraph w-full text-left"
                  data-testid="INFO_BOX_DESCRIPTION"
                >
                  {t(infoBoxDescription, { context: networkName, ...translationVariables })}{' '}
                  <a
                    data-testid="INFO_BOX_HYPERLINK"
                    className="textLinkDetail"
                    href={infoBoxLinkHref}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t(infoBoxLinkDescription, { context: networkName })}
                  </a>
                </div>
              </div>
            </div>
          )}
        </>
        <>
          {showSubDescription && subDescription && i18n.exists(subDescription, { context: networkName }) && (
            <p className="mt-4 text-left text-textParagraph" data-testid="VERIFY_IDENTITY_ACCEPT_TERMS">
              {t(subDescription, { context: networkName })}
            </p>
          )}
        </>
      </TitleContainer>

      <div className="flex flex-col w-full">
        <>
          {privacyPolicyLink && termsLink && (
            <div className="flex items-center mb-6">
              <input
                data-testid="TERMS_AND_CONDITIONS_CHECKBOX"
                type="checkbox"
                className="form-checkbox checkBox"
                onChange={handleChange(setTermsChecked)}
                checked={termsChecked}
              />
              <div className="text-sm leading-5 text-textParagraph">
                <span onClick={toggleTermsChecked} onKeyDown={toggleTermsChecked} role="button" tabIndex={0}>
                  {t('screens.start.agree')}{' '}
                </span>
                <a
                  data-testid="TERMS_AND_CONDITIONS_HYPERLINK"
                  className="textLinkDetail"
                  href={termsLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('screens.start.termsLabel')}
                </a>{' '}
                &{' '}
                <a
                  data-testid="PRIVACY_POLICY_HYPERLINK"
                  className="textLinkDetail"
                  href={privacyPolicyLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('screens.start.privacyLabel')}
                </a>
              </div>
            </div>
          )}
        </>
        <>
          {biometricPolicyLink && (
            <div className="ml-0 flex w-full justify-start mb-6">
              <input
                data-testid="BIOMETRICS_POLICY_CHECKBOX"
                type="checkbox"
                className="form-checkbox checkBox"
                onChange={handleChange(setBiometricsPolicyChecked)}
                checked={biometricsPolicyChecked}
              />
              <div className="text-sm leading-5 text-textParagraph">
                <span onClick={toggleBiometricsChecked} onKeyDown={toggleBiometricsChecked} role="button" tabIndex={0}>
                  {t('screens.start.agree')}{' '}
                </span>
                <a
                  data-testid="BIOMETRICS_POLICY_HYPERLINK"
                  className="textLinkDetail"
                  href={biometricPolicyLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('screens.start.biometricsLabel')}
                </a>
              </div>
            </div>
          )}
        </>
      </div>
      <ButtonContainer bottomMargin={shouldShowSecondaryButton() ? 'mb-0' : 'mb-1'}>
        <Button
          style={ButtonStyle.DEFAULT}
          extraClasses="mb-2"
          disabled={isButtonDisabled()}
          dataTestid="START_BUTTON"
          onClick={handlePrimaryButtonClick}
        >
          <span>{t(primaryButtonLabel, { context: networkName })}</span>
        </Button>

        <>
          {shouldShowSecondaryButton() && (
            <Button
              style={ButtonStyle.INVERTED}
              disabled={isButtonDisabled()}
              onClick={handleSecondaryButtonClick}
              dataTestid="APP_BUTTON"
            >
              <span>{t(secondaryButtonLabel, { context: networkName })}</span>
            </Button>
          )}
        </>
      </ButtonContainer>

      <>
        {footerButtonLabel && (
          <ButtonContainer bottomMargin="mb-0">
            <Button
              style={ButtonStyle.INVERTED}
              disabled={false}
              onClick={handleFooterButtonClick}
              dataTestid="FOOTER_BUTTON"
            >
              <span>{t(footerButtonLabel, { context: networkName })}</span>
            </Button>
          </ButtonContainer>
        )}
      </>
    </Container>
  );
}

ExplainerPanel.defaultProps = {
  topIcon: ExplainerPanelIcon.CIVIC,
  title: null,
  description: {
    description: 'common.explainer.description',
  },
  subDescription: 'common.explainer.subDescription',
  infoBoxDescription: undefined,
  infoBoxLinkHref: undefined,
  infoBoxLinkDescription: 'common.explainer.infoBoxLinkDescription',
  hideInfoBoxIcon: false,
  primaryButtonLabel: 'common.explainer.primaryButtonLabel',
  secondaryButtonLabel: 'common.explainer.secondaryButtonLabel',
  translationVariables: {},
  onSecondaryButtonPressed: () => {},
  onBackButtonPressed: undefined,
  footerButtonLabel: undefined,
  showDescription: true,
  showSubDescription: true,
  showSecondaryButton: true,
  showBackButton: false,
  showHeaderPanel: true,
};
